// telemetry.ts — the dice game's product-telemetry surface (SC-57239).
//
// Two thin concerns over the SDK's telemetry. The SDK owns consent, identity, buffering, envelope stamping
// and the REAL ingress — this module NEVER touches the network and never decides consent:
//
//   1. telemetryContext() — the build-time { environment, appVersion } context threaded into createGameSDK
//      (bootstrap.ts) so EDGE-emitted lifecycle events carry the right env/version. These two fields are
//      client-authoritative (NOT edge-overwritten), so the host must supply them for correct attribution.
//
//   2. createGameTelemetry(sink) — typed emitters for the game-authored UI events the dice owns, each a
//      one-liner over sdk.track(). It can ONLY ever emit the two allowlisted names it hard-codes
//      (`help_opened`, `bet_changed`), so the game structurally cannot forge a server-authoritative event
//      (`round_completed` / `session_started` are impossible from here — the SDK also blocks them).

/** Deployment environment the SDK's telemetry config accepts (client-authoritative, not edge-overwritten). */
export type TelemetryEnvironment = "prod" | "staging" | "dev";

/** The build-time telemetry context passed to `createGameSDK({ telemetry })`. */
export interface TelemetryContext {
  readonly environment: TelemetryEnvironment;
  readonly appVersion: string;
}

/** Which help/fairness affordance the player opened (`help_opened.panel`). */
export type HelpPanel = "fairness" | "provably_fair" | "round_detail";

/**
 * Where a PRE-submit bet adjustment came from (`bet_changed.source`). This is the distinguishing attribute
 * that keeps the game's own UI-origin `bet_changed` apart from the SDK's AUTO post-play `bet_changed`
 * (emitted from inside the SDK on a settled `play()`), which carries no `source`.
 */
export type BetChangeSource = "input" | "chip" | "slider" | "multiplier" | "win_chance";

/** The minimal sink the game needs — structurally satisfied by `GameSdk` (its public `track`). */
export interface TrackSink {
  track(eventName: string, attributes?: Record<string, string | number | boolean>): void;
}

/** The dice's game-authored telemetry emitters. Every method routes through `sink.track` — no network here. */
export interface GameTelemetry {
  /** The player opened a help/fairness affordance. */
  helpOpened(panel: HelpPanel): void;
  /** The player adjusted the bet BEFORE submitting (stake or risk). `amountMinor` = the current stake. */
  betChanged(source: BetChangeSource, amountMinor: number): void;
}

// Injected at build time by Vite `define` (vite.config.ts). Erased/undefined under a bare vitest/Node run
// (no define step); every read below is typeof-guarded so this module never throws off-build.
declare const __DICE_TELEMETRY_ENV__: string | undefined;
declare const __DICE_APP_VERSION__: string | undefined;
declare const __DICE_BUILD_LABEL__: string | undefined;

const injectedEnv = typeof __DICE_TELEMETRY_ENV__ === "string" ? __DICE_TELEMETRY_ENV__ : undefined;
const injectedVersion = typeof __DICE_APP_VERSION__ === "string" ? __DICE_APP_VERSION__ : undefined;

/**
 * The client-authoritative telemetry context for `createGameSDK`: the build's identity, from the ref the pipeline
 * deployed (scripts/build-identity.mjs, SC-62248) — a clean release tag is prod, a release candidate is staging,
 * anything else is dev; `appVersion` is the release (the tag without its v), or 0.0.0-dev+<commit>. Both fall back to
 * safe placeholders off-build (e.g. under test), and an unknown environment is dev, never prod. `tenantId` and
 * `schemaVersion` are intentionally omitted — the dice has no client-side source for either (tenant is an
 * edge-overwritten hint; schema version is stamped by the SDK), so the SDK's safe defaults apply.
 */
export function telemetryContext(): TelemetryContext {
  const environment: TelemetryEnvironment =
    injectedEnv === "prod" || injectedEnv === "staging" || injectedEnv === "dev" ? injectedEnv : "dev";
  return { environment, appVersion: injectedVersion ?? "0.0.0" };
}

/** How this build names itself in one line — "1.0.3-rc2 (staging)" (SC-62248). Injected whole at build time, so the
 *  exact text is in the bundle (scripts/check-dist.mjs looks for it); off-build it is made from the context above. */
export function buildLabel(): string {
  if (typeof __DICE_BUILD_LABEL__ === "string") return __DICE_BUILD_LABEL__;
  const context = telemetryContext();
  return `${context.appVersion} (${context.environment})`;
}

/** Build the game's telemetry emitters over a track sink (the live `GameSdk`, or a spy in tests). */
export function createGameTelemetry(sink: TrackSink): GameTelemetry {
  return {
    helpOpened(panel) {
      sink.track("help_opened", { panel });
    },
    betChanged(source, amountMinor) {
      sink.track("bet_changed", { source, amount_minor: amountMinor });
    },
  };
}
